import { Fragment, useState, type FormEvent } from "react";
import { useApi } from "../lib/useApi";
import { ApiError, type BulkInviteResult } from "../lib/api";
import { isAcceptableDomain } from "../lib/domain";
import { Modal } from "./Modal";
import { EmailChipsInput, EMAIL_RE, splitEmails } from "./EmailChipsInput";

/**
 * Staff dialog launched from an organization: stage several email addresses, pick a role, and
 * optionally set the org's verified domain. On submit, everyone is invited (each invite attributed
 * to the org's existing administrator) and — if a domain is given — the org's ChargeBee
 * cf_verified_domain is set, which blocks that email domain from self-registering.
 */
export function InviteToOrgDialog({ orgId, orgName, onClose, onDone }: {
  orgId: string;
  orgName: string;
  onClose: () => void;
  onDone?: () => void;
}) {
  const api = useApi();
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [role, setRole] = useState("Member");
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkInviteResult | null>(null);
  const [domainNote, setDomainNote] = useState<string | null>(null);

  const domainTrim = domain.trim();
  // Fold any half-typed address in the input into the batch for the enable/label checks.
  const stagedCount = new Set([...emails, ...splitEmails(emailInput)]).size;
  const domainOnly = stagedCount === 0 && domainTrim !== "";
  const domainInvalid = !isAcceptableDomain(domain); // blank is acceptable (means "leave unchanged" here)
  const hasSomething = stagedCount > 0 || domainTrim !== "";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (domainInvalid) { setError("Enter a valid domain like company.com."); return; }
    const all = Array.from(new Set([...emails, ...splitEmails(emailInput)]));

    // No users staged: if the domain field is non-blank, do a domain-only change; otherwise nothing.
    if (all.length === 0) {
      if (domainTrim === "") { setError("Add at least one email address, or a domain to set."); return; }
      setBusy(true); setError(null); setResult(null); setDomainNote(null);
      try {
        await api.setOrganizationVerifiedDomain(orgId, domainTrim);
        setDomainNote(`Verified domain set to ${domainTrim}.`);
        onDone?.();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : `${err}`);
      } finally {
        setBusy(false);
      }
      return;
    }

    const bad = all.filter((x) => !EMAIL_RE.test(x));
    if (bad.length > 0) { setError(`Invalid email address(es): ${bad.join(", ")}`); return; }

    setBusy(true);
    setError(null);
    setResult(null);
    setDomainNote(null);
    try {
      const res = await api.bulkInviteToOrganization(orgId, {
        emails: all,
        role,
        domain: domainTrim ? domainTrim : undefined,
      });
      setResult(res);
      setEmails([]);
      setEmailInput("");
      onDone?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `${err}`);
    } finally {
      setBusy(false);
    }
  }

  const counts = result && {
    invited: result.results.filter((r) => r.status === "invited").length,
    skipped: result.results.filter((r) => r.status === "skipped").length,
    error: result.results.filter((r) => r.status === "error").length,
  };

  return (
    <Modal title={`Invite to ${orgName}`} onClose={onClose}>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 440 }}>
        {/* ---- Email staging ---- */}
        <div>
          <div className="rail-label" style={{ marginBottom: 8 }}>Email addresses</div>
          <EmailChipsInput emails={emails} onEmailsChange={setEmails} input={emailInput} onInputChange={setEmailInput} autoFocus />
        </div>

        {/* ---- Role ---- */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="rail-label">Role</div>
          <select value={role} onChange={(e) => setRole(e.target.value)} style={{ width: "auto", fontSize: 12, padding: "7px 28px 7px 10px" }}>
            <option value="Member">Member</option>
            <option value="Administrator">Administrator</option>
          </select>
        </div>

        {/* ---- Verified domain ---- */}
        <div>
          <div className="rail-label" style={{ marginBottom: 8 }}>Verified domain <span className="faint">(optional)</span></div>
          <input
            type="text"
            placeholder="company.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            spellCheck={false}
            style={{ width: "100%", fontSize: 12, padding: "8px 10px" }}
          />
          <div className="hint" style={{ marginTop: 5 }}>
            When set, anyone with this email domain is blocked from self-registering a new organization — they must be invited here.
          </div>
          {domainInvalid && (
            <div className="hint" style={{ color: "var(--red)", marginTop: 5 }}>Enter a bare domain like <span className="mono">company.com</span> (no https://, path or spaces).</div>
          )}
        </div>

        {error && (
          <div className="callout error"><span className="mk">err</span><span>{error}</span></div>
        )}

        {domainNote && (
          <div className="callout"><span className="mk">ok</span><span style={{ fontSize: 11 }}>{domainNote}</span></div>
        )}

        {result && counts && (
          <>
            <div className="callout">
              <span className="mk">ok</span>
              <span style={{ fontSize: 11 }}>
                {counts.invited} invited, {counts.skipped} skipped, {counts.error} error
                {result.domain && ` · domain ${result.domain.status}${result.domain.error ? ` (${result.domain.error})` : ""}`}
              </span>
            </div>
            <dl className="spec spec-kv" style={{ fontSize: 11 }}>
              {result.results.map((r) => (
                <Fragment key={r.email}>
                  <dt className="mono" title={r.email}>{r.email}</dt>
                  <dd className="mono" style={{ color: r.status === "invited" ? "var(--lime)" : r.status === "error" ? "var(--red)" : undefined }}>
                    {r.status}{r.error ? ` — ${r.error}` : ""}
                  </dd>
                </Fragment>
              ))}
            </dl>
          </>
        )}

        <div className="btn-row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          <button type="submit" className={"btn btn-primary btn-sm" + (busy ? " loading" : "")} disabled={busy || !hasSomething || domainInvalid}>
            {result ? "Send more" : domainOnly ? "Set domain" : "Send invites"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
